import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { clientApi } from '../api/client';

export function LoginPage() {
  const { isAuthenticated, isLoading, setApiKeyAuth } = useAuthStore();
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const validateMutation = useMutation({
    mutationFn: async (key: string) => {
      // Temporarily set the key to test it
      setApiKeyAuth(key);
      try {
        // Try to fetch templates to validate the key
        await clientApi.getTemplates();
        return true;
      } catch (err) {
        // Clear on failure
        useAuthStore.getState().logout();
        throw err;
      }
    },
    onSuccess: () => {
      navigate('/');
    },
    onError: (err: Error) => {
      setError(err.message || 'Invalid API key');
    },
  });

  const handleApiKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (apiKey.trim()) {
      validateMutation.mutate(apiKey.trim());
    }
  };

  const handleAadLogin = () => {
    // Redirect to Azure AD login via Static Web Apps
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=/';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🏷️</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            EDI Label Service
          </h1>
          <p className="text-gray-600">
            Multi-tenant label templating and generation platform
          </p>
        </div>

        {/* API Key Form */}
        <form onSubmit={handleApiKeySubmit} className="space-y-4">
            <div>
              <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">
                API Key
              </label>
              <input
                type="password"
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="elk_..."
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Your API key starts with "elk_"
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={validateMutation.isPending || !apiKey.trim()}
              className="w-full btn btn-primary py-3 text-lg"
            >
              {validateMutation.isPending ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Validating...
                </span>
              ) : (
                'Sign In with API Key'
              )}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAadLogin}
              className="w-full btn btn-secondary py-3 text-lg flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
                <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
              </svg>
              <span>Sign in with Microsoft (Admin)</span>
            </button>
          </form>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>By signing in, you agree to our terms of service.</p>
        </div>

        <div className="mt-4 text-center text-xs text-gray-400">
          <p>Test API Key: <code className="bg-gray-100 px-1 rounded">elk_test_12345678901234567890123456789012</code></p>
        </div>
      </div>
    </div>
  );
}
